import { useMemo } from 'react';
import { Link, useRouteMatch } from 'react-router-dom';
import { useSuspenseQuery } from '@tanstack/react-query';
import {
    createColumnHelper,
    getCoreRowModel,
    getExpandedRowModel,
    useReactTable,
} from '@tanstack/react-table';
import * as Avatar from '@givebutter/primitives/avatar';
import { Badge } from '@givebutter/primitives/badge';
import { Box } from '@givebutter/primitives/box';
import { Button } from '@givebutter/primitives/button';
import { Label } from '@givebutter/primitives/label';
import { maskWholeNumber } from '@givebutter/util';
import {
    faChartLineUp,
    faEnvelope,
    faPlus,
    faShare,
    faTrash,
} from '@fortawesome/pro-regular-svg-icons';
import { faCircleNotch } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { fetchReports } from 'api/reports';
import Content from 'assets/js/components/Content';
import * as DeleteReportDialog from 'assets/js/pages/reports/DeleteReportDialog';
import { getReportResourceType } from 'assets/js/pages/reports/util';
import * as DropdownMenu from 'components/dropdown-menu';
import { ClearFiltersButton } from 'components/filter/ClearFiltersButton';
import { FilterGroupProvider } from 'components/filter/filters-next/FilterGroupContext';
import { FilterProvider } from 'components/filter/filters-next/FilterProvider';
import { QuickFiltersBar } from 'components/filter/filters-next/QuickFiltersBar';
import { useFiltersByKey } from 'components/filter/filters-next/useFiltersByKey';
import * as Input from 'components/input';
import * as Table from 'components/table';
import { useSyncPaginatedData } from 'components/table/useSyncPaginatedData';
import { EmptyData } from 'components/util';
import { getAvatarFallbackColor } from 'components/util/getAvatarFallbackColor';
import { IntercomProductTourTargets } from 'constants/intercom';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import useDebounce from 'customHooks/useDebounce';
import { useIsPending } from 'customHooks/useIsPending';
import { usePersistentSearch } from 'customHooks/usePersistentSearch';
import { dateFromServer, isoDateFromServer } from 'helpers/dates';
import { getInitialsFromName } from 'helpers/formatting';
import Logo from 'images/logo-horizontal.svg?react';
import { isEmpty } from 'lodash';
import { DateTime } from 'luxon';
import { accountQueryKeys } from 'queries/account-query-keys';
import { Report } from 'types/domain';

const columnHelper = createColumnHelper<Report>();

const ReportsIndex = () => {
    const { accountId } = useCurrentAccount();
    const match = useRouteMatch();
    const [search, setSearch] = usePersistentSearch();
    const searchTerm = useDebounce(search);
    const {
        expanded,
        pagination,
        setPagination,
        setExpanded,
        sorting,
        setSorting,
        columnVisibility,
        setColumnVisibility,
        columnPinning,
        setColumnPinning,
    } = Table.useReactTableState({
        sortingState: [{ id: 'updated_at', desc: true }],
    });
    const { filters } = useFiltersByKey('reports');
    const [isPending, params] = useIsPending(
        {
            page: pagination.pageIndex + 1,
            per_page: pagination.pageSize,
            filters,
            query: searchTerm,
            ...(sorting.length > 0
                ? { [`sortBy${sorting[0].desc ? 'Desc' : ''}`]: sorting[0].id }
                : {}),
        },
        500,
    );

    const { data } = useSuspenseQuery({
        queryKey: accountQueryKeys.reportsWithFilters(accountId, params),
        queryFn: () => fetchReports(accountId, params),
    });

    const columns = useMemo(
        () => [
            columnHelper.accessor('name', {
                header: 'Report name',
                cell: info => <p className={'font-medium'}>{info.getValue()}</p>,
                meta: {
                    sortType: 'alpha',
                },
            }),
            columnHelper.accessor('resource', {
                header: 'Type',
                cell: info => (
                    <Badge size="sm" variant="rounded">
                        {getReportResourceType(info.getValue())}
                    </Badge>
                ),
            }),
            columnHelper.accessor('last_sent_at', {
                header: 'Last sent',
                cell: info => {
                    const date = info.getValue();

                    if (date) {
                        return dateFromServer(date).toLocaleString(DateTime.DATETIME_MED);
                    }

                    return <EmptyData />;
                },
                meta: {
                    sortType: 'date',
                },
            }),
            columnHelper.accessor('updated_at', {
                header: 'Updated at',
                cell: info => {
                    const date = info.getValue();

                    return isoDateFromServer(date).toLocaleString(DateTime.DATETIME_MED);
                },
                meta: {
                    sortType: 'date',
                },
            }),
            columnHelper.accessor('created_by', {
                header: 'Created by',
                cell: info => {
                    const createdBy = info.getValue();

                    if (!createdBy) {
                        return <Logo />;
                    }

                    return (
                        <div className={'flex gap-1'}>
                            <Avatar.Root size="2xs">
                                <Avatar.Fallback
                                    className={getAvatarFallbackColor(createdBy.first_name)}
                                >
                                    {getInitialsFromName(createdBy.first_name, createdBy.last_name)}
                                </Avatar.Fallback>
                            </Avatar.Root>
                            <p className={'font-medium'}>
                                {createdBy.first_name} {createdBy.last_name}
                            </p>
                        </div>
                    );
                },
                meta: {
                    sortType: 'alpha',
                },
            }),
            columnHelper.display({
                id: Table.Constants.Actions,
                cell: info => {
                    const report = info.row.original;
                    return (
                        <DropdownMenu.Root key={report.id}>
                            <Table.TableRowDropdownTrigger />
                            <DropdownMenu.Portal>
                                <DropdownMenu.Content
                                    onSelect={e => e.preventDefault()}
                                    sideOffset={5}
                                    align={'end'}
                                >
                                    <DropdownMenu.LinkItem
                                        contentClassName={'flex items-center gap-1.5'}
                                        to={`${match.url}/${report.id}/schedules/new`}
                                    >
                                        <FontAwesomeIcon icon={faShare} />
                                        Share report
                                    </DropdownMenu.LinkItem>
                                    <DropdownMenu.LinkItem
                                        to={`${match.url}/${report.id}/schedules`}
                                        contentClassName={'flex items-center gap-1.5'}
                                        disabled={report.schedules_count === 0}
                                    >
                                        <FontAwesomeIcon icon={faEnvelope} />
                                        Manage shared emails
                                    </DropdownMenu.LinkItem>
                                    {!report.is_default_template && (
                                        <DeleteReportDialog.Root reportId={report.id}>
                                            <DeleteReportDialog.Trigger asChild={true}>
                                                <DropdownMenu.Item
                                                    className={'flex items-center gap-1.5'}
                                                    onSelect={e => e.preventDefault()}
                                                >
                                                    <FontAwesomeIcon icon={faTrash} />
                                                    Delete report
                                                </DropdownMenu.Item>
                                            </DeleteReportDialog.Trigger>
                                        </DeleteReportDialog.Root>
                                    )}
                                </DropdownMenu.Content>
                            </DropdownMenu.Portal>
                        </DropdownMenu.Root>
                    );
                },
                meta: {
                    ignoreLink: true,
                },
            }),
        ],
        [match.url],
    );

    const tableState = {
        pagination,
        expanded,
        sorting,
        columnVisibility,
        columnPinning,
    };

    const table = useReactTable({
        data: data?.data ?? [],
        columns,
        pageCount: data?.meta.last_page ?? -1,
        getCoreRowModel: getCoreRowModel(),
        getExpandedRowModel: getExpandedRowModel(),
        initialState: tableState,
        state: tableState,
        onPaginationChange: setPagination,
        onExpandedChange: setExpanded,
        onSortingChange: setSorting,
        onColumnVisibilityChange: setColumnVisibility,
        onColumnPinningChange: setColumnPinning,
        manualPagination: true,
        enableColumnPinning: false,
        enableHiding: false,
        meta: {
            enableColumnOrder: false,
            to: cell => `/accounts/${accountId}/reports/${cell.row.original.id}`,
        },
    });

    useSyncPaginatedData(table, data);

    return (
        <FilterProvider cacheKey={'reports'} resource={'reports'}>
            <Content className={'flex flex-col gap-4'}>
                <div className={'flex items-center justify-between gap-4'}>
                    <div className={'flex items-center gap-x-4'}>
                        <h1 className={'text-2xl font-medium leading-none'}>Custom Reports</h1>
                        <div className={'h-6 w-px bg-gray-300'} />
                        {isPending ? (
                            <FontAwesomeIcon className={'animate-spin'} icon={faCircleNotch} />
                        ) : (
                            <p className={'text-base font-medium leading-none'}>
                                {data?.meta.total ? maskWholeNumber(data.meta.total) : 0}
                            </p>
                        )}
                    </div>
                    <Link
                        to={`/accounts/${accountId}/reports/new`}
                        data-intercom-target={IntercomProductTourTargets.ReportsNewReportButton}
                    >
                        <Button variant="solid">
                            <FontAwesomeIcon icon={faPlus} />
                            New report
                        </Button>
                    </Link>
                </div>
                <Table.Root
                    isLoading={isPending}
                    table={table}
                    searchTerm={searchTerm}
                    variant="bordered"
                    isFiltered={!isEmpty(filters)}
                >
                    <Box className="flex flex-col">
                        <div
                            className={'flex justify-between gap-4 border-b border-b-gray-100 p-4'}
                        >
                            <div className={'flex flex-wrap gap-1.5'}>
                                <Label className="sr-only" htmlFor="reports_search">
                                    Search
                                </Label>
                                <Input.Root size={'sm'}>
                                    <Input.SearchIcon />
                                    <Input.Form
                                        id="reports_search"
                                        placeholder={'Search'}
                                        value={search}
                                        onChange={e => setSearch(e.target.value)}
                                    />
                                </Input.Root>
                            </div>
                            <div className={'flex gap-1'}>
                                <Table.ToggleFullWidth />
                                <Table.Reset />
                            </div>
                        </div>
                        <div className={'flex flex-wrap items-center gap-2 px-4 py-3'}>
                            <FilterGroupProvider
                                showInnerOperator={false}
                                showOuterOperator={false}
                            >
                                <QuickFiltersBar quickFilters={['created_by', 'resource']} />
                                <ClearFiltersButton />
                            </FilterGroupProvider>
                        </div>
                        <Table.Content>
                            <Table.Header />
                            <Table.Body />
                        </Table.Content>
                        <Table.Empty variant={'primary'} className="max-w-full gap-6">
                            <Table.EmptyIcon>
                                <FontAwesomeIcon icon={faChartLineUp} />
                            </Table.EmptyIcon>
                            <Table.EmptyTitle>No reports found</Table.EmptyTitle>
                        </Table.Empty>
                        <Table.Pagination className={'border-t border-t-gray-100 p-2'}>
                            <Table.PaginationPageNavigation />
                            <Table.PaginationPerPageDropdown />
                        </Table.Pagination>
                    </Box>
                </Table.Root>
            </Content>
        </FilterProvider>
    );
};

export { ReportsIndex };
